import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Button from '../components/ui/Button';

const FadeUp = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-100px" }}
    transition={{ duration: 1, delay, ease: [0.21, 0.47, 0.32, 0.98] }}
    className={className}
  >
    {children}
  </motion.div>
);

export default function Resources() {
  const BOOKING_URL = "https://link.nordiccrm.ca/widget/bookings/dorman-ernest-baltazar-personal-calendar-qg2bbc8wa";

  const resources = [
    { title: "The Calling vs Career Clarity Guide", category: "Free Guide", link: "/clarity-guide", description: "A reflective framework helping creators identify the gap between external success and internal alignment.", image: "https://images.unsplash.com/photo-1455390582262-044cdead277a?auto=format&fit=crop&q=80&w=800" },
    { title: "The Creative Kingdom Blueprint", category: "Framework", link: "/blueprint", description: "The flagship systems-based framework for building sustainable creator businesses rooted in identity.", image: "https://images.unsplash.com/photo-1494438639946-1ebd1d20bf85?auto=format&fit=crop&q=80&w=800" },
    { title: "THRIVE", category: "Wellness", link: "/thrive", description: "A wholistic wellness guide helping entrepreneurs protect their energy, clarity, and longevity.", image: "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?auto=format&fit=crop&q=80&w=800" },
    { title: "Creator Philosophy Essays", category: "Reflections", link: "#", description: "Deep dives into the intersection of faith, creation, and modern entrepreneurship.", image: "https://images.unsplash.com/photo-1476081718509-b5d0b6e532ee?auto=format&fit=crop&q=80&w=800" },
    { title: "Wellness Reflections", category: "Practice", link: "#", description: "Guided prompts and meditations for the busy visionary seeking to remain whole.", image: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&q=80&w=800" },
    { title: "Systems & Sustainability Frameworks", category: "Tools", link: "#", description: "Actionable templates for operationalizing stewardship within your creative brand.", image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=800" }
  ];

  return (
    <div className="w-full bg-kin-black">
      <section className="relative pt-48 pb-32 px-6 overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-kin-gold/5 to-transparent pointer-events-none"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <FadeUp>
            <p className="text-kin-gold tracking-[0.4em] text-xs uppercase font-light mb-8"> Resources For Sustainable Creators </p>
          </FadeUp>
          <FadeUp delay={0.2}>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif text-kin-ivory mb-10 leading-[1.1]"> Tools, Frameworks, And <br className="hidden lg:block" /> <span className="italic text-kin-gold">Reflections</span> For Purpose-Driven Builders. </h1>
          </FadeUp>
          <FadeUp delay={0.3}>
            <p className="max-w-2xl text-lg md:text-xl text-kin-ivory/50 font-light leading-relaxed tracking-wide"> Explore resources designed to help creators build with alignment, clarity, stewardship, and sustainability. </p>
          </FadeUp>
        </div>
      </section>

      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-20 gap-x-12">
            {resources.map((res, i) => (
              <FadeUp key={i} delay={i * 0.1}>
                <Link to={res.link} className="group block">
                  <div className="relative aspect-[16/10] overflow-hidden mb-10 bg-[#16161a]">
                    <img src={res.image} alt={res.title} className="w-full h-full object-cover opacity-40 grayscale group-hover:scale-105 group-hover:opacity-60 transition-all duration-1000 ease-out" />
                    <div className="absolute inset-0 border border-kin-ivory/5 group-hover:border-kin-gold/30 transition-colors duration-500"></div>
                  </div>
                  <div className="space-y-4">
                    <p className="text-[10px] uppercase tracking-[0.3em] text-kin-gold font-semibold"> {res.category} </p>
                    <h3 className="text-2xl font-serif text-kin-ivory group-hover:text-kin-gold transition-colors duration-500"> {res.title} </h3>
                    <p className="text-sm text-kin-ivory/40 font-light leading-relaxed line-clamp-2"> {res.description} </p>
                    <div className="pt-4 flex items-center gap-3 text-[10px] uppercase tracking-widest text-kin-ivory/60 group-hover:text-kin-ivory transition-colors"> View Resource <div className="h-px w-8 bg-kin-ivory/20 group-hover:w-12 group-hover:bg-kin-gold transition-all duration-500"></div> </div>
                  </div>
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* GUIDANCE CTA (NEW) */}
      <section className="py-32 md:py-48 px-6 bg-[#09090b] border-y border-kin-ivory/5 text-center">
        <div className="max-w-4xl mx-auto">
          <FadeUp>
            <h2 className="text-3xl md:text-5xl font-serif text-kin-ivory mb-8">
              Need Personalized <span className="italic text-kin-gold">Guidance?</span>
            </h2>
            <p className="max-w-2xl mx-auto text-lg md:text-xl text-kin-ivory/50 font-light leading-relaxed tracking-wide mb-12">
              Schedule a one-on-one discovery conversation with Dorman and explore the next step for your journey, business, or creative vision.
            </p>
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
              <Button variant="primary" className="px-16 py-6 text-sm"> Book A Discovery Call </Button>
            </a>
          </FadeUp>
        </div>
      </section>

      <section className="py-40 md:py-60 px-6 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-[#0C0C0F]"></div>
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full h-px bg-gradient-to-r from-transparent via-kin-gold/20 to-transparent"></div>
        <div className="max-w-4xl mx-auto relative z-10">
          <FadeUp>
            <h2 className="text-4xl md:text-6xl font-serif text-kin-ivory mb-16 italic"> Build Something That Lasts. </h2>
          </FadeUp>
          <FadeUp delay={0.2}>
            <Link to="/">
              <Button variant="primary" className="px-16 py-6 text-sm"> Enter The Ecosystem </Button>
            </Link>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}